/**
 * Drop-in replacement for React.lazy that recovers from stale-chunk failures.
 *
 * After a deploy, a long-lived tab still runs the old index.html, which
 * references asset hashes that no longer exist on S3. The next route-level
 * dynamic import then 404s with a ChunkLoadError. This wrapper:
 *   1. retries the import once (covers a transient network blip), then
 *   2. reloads the page once to pull a fresh index.html with current hashes.
 *
 * A sessionStorage flag guards against an infinite reload loop when the chunk
 * is genuinely missing (e.g. a real build error); the flag is cleared on any
 * successful import so a later legitimate stale-chunk reload can still fire.
 *
 * This is the web counterpart to the cache-control invariant in
 * docs/infrastructure/ci-pipeline-and-hooks.md: old hashed chunks linger on S3
 * (never `--delete`d) as a grace buffer, and the reload covers the window after
 * that buffer or when index.html itself was already refreshed.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * Session key recording that the one-shot recovery reload has been spent.
 *
 * Exported because the app-level ErrorBoundary — the screen a spent budget
 * lands the user on — has to be able to give it back; see
 * `clearLazyRetryReloadFlag`.
 */
export const LAZY_RETRY_RELOAD_FLAG = 'panluma:lazy-retry-reloaded';

/**
 * Re-arm the one-shot recovery reload.
 *
 * The flag exists to stop an AUTOMATIC reload loop when a chunk is genuinely
 * gone. A person pressing "Reload page" on the error boundary is not that loop
 * — and without this, that button is inert: the import fails, the flag is
 * already set, and the boundary re-renders instantly, so the tab is stuck on
 * the error screen for the rest of the session even once the chunk is
 * fetchable again (WI ad05f876).
 */
export function clearLazyRetryReloadFlag(): void {
  sessionStorage.removeItem(LAZY_RETRY_RELOAD_FLAG);
}

// Mirror React.lazy's own signature exactly. `ComponentType<unknown>` would
// reject components with required props (props are contravariant), so — like
// React's built-in typings — we use `any` for the props parameter here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await factory();
      sessionStorage.removeItem(LAZY_RETRY_RELOAD_FLAG);
      return module;
    } catch {
      // One retry for a transient network blip before assuming a stale chunk.
      try {
        const module = await factory();
        sessionStorage.removeItem(LAZY_RETRY_RELOAD_FLAG);
        return module;
      } catch (retryError) {
        // Likely a stale chunk after a deploy. Reload once to fetch a fresh
        // index.html with current asset hashes — but only once, to avoid an
        // infinite reload loop if the chunk is genuinely gone.
        if (!sessionStorage.getItem(LAZY_RETRY_RELOAD_FLAG)) {
          sessionStorage.setItem(LAZY_RETRY_RELOAD_FLAG, '1');
          window.location.reload();
          // The reload replaces the page; never resolve so React doesn't flash
          // an error boundary before the navigation completes.
          return new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
